import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import NProgress from 'nprogress';

import { authApi } from '@/utilities/axios';
import { useAuthStore } from '@/modules/auth/store';
import { showToast } from '@/utilities/toast-handler';
import { CompanyType } from '../enum';
import { hasCompanyFeature, setCompanyPreferences } from '@/product/ams/utils';
import { userCompanyPreference } from '@/composables/CompanyPreferenceComposable';

const COMPANY_URL = process.env.VUE_APP_API_VERSION + '/admin/companies';

const COMPANY_TYPES = [
    { id: '1', name: CompanyType.FIRE_AND_RESCUE },
    { id: '2', name: CompanyType.POLICE },
    { id: '3', name: CompanyType.NHS },
];

export const useCompanyStore = defineStore('company', () => {
    const authStore = useAuthStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });
    const activeCompany = ref(useLocalStorage('activeCompany', {}));
    const loading = ref(false);
    const editId = ref(null);
    const company = ref({
        name: null,
        type: null,
        logo_id: null, // atm logo upload is not yet configured
    });
    const companies = ref([]);
    const companyTypes = ref(COMPANY_TYPES);
    const companyPreferences = ref({});

    // const toast = useToast();
    watch(activeCompany, () => {
        if (activeCompany.value?.id) {
            switchActiveCompany();
        }
    });

    // helper functions
    // this is only for root user where company is required to be switched since root user doesnt come with a company id
    function switchActiveCompany() {
        // set root selected company
        authStore.selectCompany(activeCompany.value?.id);
    }

    function getCompanyType(typeId) {
        return COMPANY_TYPES?.find((type) => type.id == typeId)?.name;
    }

    const assuranceColors = computed(() => {
        const prefs = companyPreferences.value[company_id.value];
        if (!prefs?.assurance_colors_data) return {};
        try {
            const raw = prefs.assurance_colors_data;
            const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
            const colors = {};
            for (const key of Object.keys(data)) {
                const entry = data[key];
                // New format: {color, label} object; old format: plain hex string
                const hex = typeof entry === 'object' ? entry.color : entry;
                colors[key] = hex && !hex.startsWith('#') ? '#' + hex : hex;
            }
            return colors;
        } catch {
            return {};
        }
    });

    const assuranceLabels = computed(() => {
        const prefs = companyPreferences.value[company_id.value];
        if (!prefs?.assurance_colors_data) return {};
        try {
            const raw = prefs.assurance_colors_data;
            const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
            const labels = {};
            for (const key of Object.keys(data)) {
                const entry = data[key];
                if (typeof entry === 'object' && entry.label) {
                    labels[key] = entry.label;
                }
            }
            return labels;
        } catch {
            return {};
        }
    });

    function cleanCache() {
        activeCompany.value = {};
    }

    function initCompanyDetails() {
        company.value = {
            name: null,
            type: null,
            logo_id: null, // atm logo upload is not yet configured
        };
        editId.value = null;
    }
    // actions
    const getCompanies = async () => {
        try {
            NProgress.start();
            loading.value = true;
            const response = await authApi.get(`${COMPANY_URL}`);
            if (response.statusCode === 200) {
                companies.value = response.data;
                loading.value = false;
                return companies.value;
            }
        } catch (error) {
            showToast({}, 'Failed to fetch companies');
        } finally {
            NProgress.done();
        }
    };

    const addCompany = async (company) => {
        try {
            NProgress.start();
            const response = await authApi.post(`${COMPANY_URL}`, company);
            if (response.statusCode === 201) {
                showToast(response.toast, 'Company added successfully');
                companies.value.push(response.data);
            }
            return response.data;
        } catch (error) {
            showToast({}, error.data.message ?? 'Failed to add company');
        } finally {
            NProgress.done();
        }
    };

    const editCompany = (data) => {
        // console.log(company);
        editId.value = data.id;
        company.value.name = data.name;
        company.value.type = data.type;
        company.value.logo_id = data.logo_id;
    };

    const updateCompany = async (id, company) => {
        try {
            NProgress.start();
            const response = await authApi.patch(`${COMPANY_URL}/${id}`, company);
            if (response.statusCode === 200) {
                showToast(response.toast, 'Company updated successfully');
                // debugger;
                // replace the updated company inside the companies array
                const index = companies.value.findIndex((company) => company.id === response.data.id);
                // Once the matching index is found, replace the company with the updated one
                if (index !== -1) {
                    companies.value[index] = response.data;
                }
            }
            return response.data;
        } catch (error) {
            showToast({}, error.data.message ?? 'Failed to update company');
        } finally {
            NProgress.done();
        }
    };

    const deleteCompany = async (id) => {
        try {
            NProgress.start();
            const response = await authApi.delete(`${COMPANY_URL}/${id}`);
            if (response.statusCode === 200) {
                showToast(response.toast, 'Company deleted');
                companies.value = companies.value?.filter((company) => company.id !== id);
            }
        } catch (error) {
            showToast({}, error.data.message ?? 'Failed to delete company');
        } finally {
            NProgress.done();
        }
    };

    async function fetchCompanyPreferences() {
        if (!company_id.value) return;
        const prefs = await userCompanyPreference().fetchCompanyPreference(company_id.value);
        if (prefs) {
            companyPreferences.value = { ...companyPreferences.value, [company_id.value]: prefs };
            setCompanyPreferences(company_id.value, prefs);
        }
    }

    function useFeatureAccess(featureKey) {
        return computed(() => {
            // Read reactive ref to establish dependency so computed re-evaluates
            // when preferences are loaded after mount
            const prefs = companyPreferences.value[company_id.value];
            if (!prefs) return false;
            return hasCompanyFeature(company_id.value, featureKey);
        });
    }


    return {
        // states
        loading,
        activeCompany,
        editId,
        company,
        companies,
        companyTypes,
        companyPreferences,
        // actions
        getCompanies,
        addCompany,
        editCompany,
        updateCompany,
        deleteCompany,
        fetchCompanyPreferences,
        assuranceColors,
        assuranceLabels,
        useFeatureAccess,
        // helpers
        getCompanyType,
        initCompanyDetails,
        cleanCache,
    };
});
